import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useRef, useState } from "react";
import { HistoricNarService } from "../../../../../../shared/api/services/historicNar/historicNarService";
import { HistoricNarClearReason } from "../../../../../../shared/api/services/historicNar/types";
import { logError } from "../../../../../utils/customLogger";

interface UseNeedsReviewOptions {
  identifier?: string;
  onClose: () => void;
  onReIngest: () => void;
}

type PendingAction = "save" | "reingest";

/** Own Needs Review selection, loading state, cancellation, and submission. */
const useNeedsReview = ({
  identifier,
  onClose,
  onReIngest,
}: UseNeedsReviewOptions) => {
  const selectedReasonRef = useRef<HistoricNarClearReason | null>(null);
  const clearRequestRef = useRef<AbortController>();
  const [hasSelectedReason, setHasSelectedReason] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null,
  );

  useEffect(() => () => clearRequestRef.current?.abort(), []);

  const clearLane = async (action: PendingAction, onSuccess: () => void) => {
    const reason = selectedReasonRef.current;
    if (!identifier || !reason || clearRequestRef.current) return;
    const controller = new AbortController();
    clearRequestRef.current = controller;
    setPendingAction(action);
    try {
      const response = await HistoricNarService.clearNarLane(
        { identifier, reason },
        controller.signal,
      );
      if (controller.signal.aborted) return;
      if (!response.cleared) {
        toast.error("Failed to clear Needs Review lane");
        return;
      }
      toast.success("Needs Review lane cleared");
      onSuccess();
    } catch (error) {
      if (controller.signal.aborted) return;
      logError(error as Error, "Error clearing Historic NAR Needs Review lane");
      toast.error("Error clearing Needs Review lane");
    } finally {
      if (!controller.signal.aborted) setPendingAction(null);
      if (clearRequestRef.current === controller)
        clearRequestRef.current = undefined;
    }
  };

  const isSubmitting = pendingAction !== null;
  return {
    isSubmitting,
    disableActions: !identifier || !hasSelectedReason || isSubmitting,
    saveButtonText: pendingAction === "reingest" ? "Clearing..." : "Re-Ingest",
    cancelText: pendingAction === "save" ? "Saving..." : "Save",
    handleReasonChange: (value: string) => {
      selectedReasonRef.current = value
        ? (value as HistoricNarClearReason)
        : null;
      setHasSelectedReason(Boolean(value));
    },
    handleSave: () => clearLane("save", onClose),
    handleReIngest: () => clearLane("reingest", onReIngest),
  };
};

export default useNeedsReview;
